import type { StylePack } from "./render";

/**
 * Resolve a StylePack from the live host DOM.
 *
 * Live testing on a real store showed that named accent tokens are often absent
 * (a theme may expose text/surface/border but no `--*-primary`). Relying on a
 * variable name alone leaves the CTA and featured states uncolored. So we probe
 * by name FIRST, then fall back to reading the accent from what the theme actually
 * rendered — a real CTA button's background — and finally to the text color for
 * monochrome themes. The accent is always derived from the theme, never a constant.
 */

/** Canonical --gem-* token → ordered host-var candidates (mirrors theme-adapter). */
export const TOKEN_SOURCES: Record<string, string[]> = {
  "--gem-color-primary": ["--gem-color-primary", "--gemforge-primary", "--wn-primary", "--color-primary", "--color-button"],
  "--gem-color-primary-hover": ["--gem-color-primary-hover", "--gemforge-primary-dark", "--wn-primary-dark"],
  "--gem-color-text": ["--gem-color-text", "--gemforge-text", "--wn-text", "--color-foreground"],
  "--gem-color-surface": ["--gem-color-surface", "--gemforge-surface", "--color-background"],
  "--gem-color-surface-alt": ["--gem-color-surface-alt", "--gemforge-surface-alt"],
  "--gem-color-border": ["--gem-color-border", "--gemforge-border", "--wn-border"],
  "--gem-color-muted": ["--gem-color-muted", "--gemforge-text-muted", "--wn-muted"],
  // Accent (featured/metric/urgency emphasis) — a theme's secondary highlight if it
  // has one; otherwise defaulted to the resolved primary below so emphasis always
  // reads in the theme's own palette, never a constant.
  "--gem-color-accent": ["--gem-color-accent", "--gemforge-accent", "--wn-accent", "--color-accent"],
  "--gem-color-success": ["--gem-color-success", "--gemforge-success", "--color-success"],
  // Roundness — a sharp/minimal theme vs a soft/playful one should carry through to
  // the surface's own cards, buttons and corners.
  "--gem-radius-sm": ["--gem-radius-sm", "--gemforge-radius-sm", "--radius-sm"],
  "--gem-radius-md": ["--gem-radius-md", "--gemforge-radius-md", "--radius", "--border-radius"],
  "--gem-radius-lg": ["--gem-radius-lg", "--gemforge-radius-lg", "--radius-lg"],
  "--gem-radius-pill": ["--gem-radius-pill", "--gem-variant-pill-radius"],
  // Heading + button character (context-independent) — so a generated headline and CTA
  // carry the store's own type treatment and button shape, matching its real sections.
  "--gem-heading-letter-spacing": ["--gem-heading-letter-spacing"],
  "--gem-heading-transform": ["--gem-heading-transform"],
  "--gem-button-border-width": ["--gem-button-border-width"],
  "--gem-font-heading": ["--gem-font-heading", "--font-heading-family"],
  "--gem-font-body": ["--gem-font-body", "--font-body-family"],
};

/** Selectors most likely to be a theme's primary call-to-action, strongest first. */
const DEFAULT_CTA_SELECTORS: string[] = [
  ".shopify-payment-button__button",
  "button[name='add']",
  ".product-form__submit",
  ".product-form button[type='submit']",
  "button.button--primary",
  ".btn--primary",
  ".button--primary",
  "a.button",
  ".button",
  ".btn",
  "button[type='submit']",
];

export interface DeriveStylePackOptions {
  ref?: string;
  root?: HTMLElement;
  doc?: Document;
  ctaSelectors?: string[];
}

/** How the accent was resolved — useful for diagnostics / telemetry. */
export type AccentOrigin = "token" | "cta_background" | "text_fallback" | "none";

export interface DerivedStylePack extends StylePack {
  accentOrigin: AccentOrigin;
}

export function deriveStylePackFromDom(options: DeriveStylePackOptions = {}): DerivedStylePack {
  const doc = options.doc ?? document;
  const win = doc.defaultView ?? window;
  const root = options.root ?? doc.documentElement;
  const rootStyle = win.getComputedStyle(root);

  const tokens: Record<string, string> = {};
  for (const [canonical, candidates] of Object.entries(TOKEN_SOURCES)) {
    const value = firstVar(rootStyle, root, candidates);
    if (!value) continue;
    if (canonical.includes("color") && !isUsableColor(doc, value)) continue;
    tokens[canonical] = value;
  }

  // Fill surface/text from <body> if the theme exposed neither.
  const bodyStyle = doc.body ? win.getComputedStyle(doc.body) : null;
  if (bodyStyle) {
    if (!tokens["--gem-color-surface"]) {
      const bg = solidColor(bodyStyle.backgroundColor);
      if (bg) tokens["--gem-color-surface"] = bg;
    }
    if (!tokens["--gem-color-text"]) {
      const fg = solidColor(bodyStyle.color);
      if (fg) tokens["--gem-color-text"] = fg;
    }
  }

  // Accent: named token → real CTA background → text color (monochrome themes).
  let accentOrigin: AccentOrigin = tokens["--gem-color-primary"] ? "token" : "none";
  if (!tokens["--gem-color-primary"]) {
    const accent = sampleAccentColor(doc, win, options.ctaSelectors ?? DEFAULT_CTA_SELECTORS, tokens["--gem-color-surface"]);
    if (accent) {
      tokens["--gem-color-primary"] = accent;
      accentOrigin = "cta_background";
    } else if (tokens["--gem-color-text"]) {
      tokens["--gem-color-primary"] = tokens["--gem-color-text"];
      accentOrigin = "text_fallback";
    }
  }

  // Emphasis accent falls back to the resolved primary so featured/metric/urgency
  // states render in THIS theme's palette rather than the CSS constant.
  if (!tokens["--gem-color-accent"] && tokens["--gem-color-primary"]) {
    tokens["--gem-color-accent"] = tokens["--gem-color-primary"];
  }

  return { ref: options.ref ?? "stylepack:derived@runtime", tokens, accentOrigin };
}

function sampleAccentColor(doc: Document, win: Window, selectors: string[], surface: string | undefined): string | null {
  for (const selector of selectors) {
    for (const node of Array.from(doc.querySelectorAll(selector))) {
      const bg = solidColor(win.getComputedStyle(node).backgroundColor);
      if (bg && !isNearColor(bg, surface)) return bg;
    }
  }
  return null;
}

function firstVar(style: CSSStyleDeclaration, root: HTMLElement, candidates: string[]): string | null {
  for (const name of candidates) {
    const raw = (style.getPropertyValue(name) || root.style.getPropertyValue(name)).trim();
    const value = normalizeValue(raw);
    if (value) return value;
  }
  return null;
}

/**
 * A bare "r g b" / "r, g, b" triplet (Shopify Dawn and every theme derived from it) becomes a usable
 * rgb() colour. Dawn stores colours as triplets so themes can write `rgb(var(--color-background) / .5)`;
 * copied verbatim into a plain `background:` they are not colours at all.
 */
function normalizeValue(raw: string): string {
  if (!raw) return "";
  const triplet = raw.match(/^(\d{1,3})[,\s]+(\d{1,3})[,\s]+(\d{1,3})$/);
  if (triplet) return `rgb(${triplet[1]}, ${triplet[2]}, ${triplet[3]})`;
  return raw;
}

/**
 * Refuse a value the browser cannot use as a colour.
 *
 * This is the guard that matters more than the normaliser above. A custom property that is DEFINED
 * but invalid is worse than one that is missing: `var(--x, #fff)` only falls back when --x is
 * undefined, so an invalid value silently computes the whole declaration to its initial value —
 * a transparent panel with the storefront showing straight through it. Adopting nothing leaves our
 * own fallback intact, which is always the safer failure.
 */
function isUsableColor(doc: Document, value: string): boolean {
  // Assigning through the CSSOM is the check that works everywhere: an invalid value simply does not
  // stick. `CSS.supports` would be tidier but is absent in some runtimes, and a guard that fails OPEN
  // reintroduces exactly the bug it exists to prevent.
  const probe = doc.createElement("span").style;
  probe.color = value;
  return probe.color !== "";
}

interface Rgb {
  r: number;
  g: number;
  b: number;
  a: number;
}

function parseColor(input: string): Rgb | null {
  const match = input.trim().match(/^rgba?\(([^)]+)\)$/i);
  const body = match?.[1];
  if (!body) return null;
  const parts = body.split(",").map((part) => Number.parseFloat(part.trim()));
  const r = parts[0];
  const g = parts[1];
  const b = parts[2];
  if (r === undefined || g === undefined || b === undefined || Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return null;
  }
  const rawA = parts[3];
  const a = rawA === undefined || Number.isNaN(rawA) ? 1 : rawA;
  return { r, g, b, a };
}

/** Returns the color string only if it is a visible (non-transparent) solid. */
function solidColor(input: string): string | null {
  const color = parseColor(input);
  if (!color || color.a <= 0) return null;
  return input.trim();
}

function isNearColor(a: string, b: string | undefined): boolean {
  if (!b) return false;
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return false;
  const distance = Math.hypot(ca.r - cb.r, ca.g - cb.g, ca.b - cb.b);
  return distance < 32;
}
